import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid/index.js'
import type { EItemType, FileForProcessing, IOrder, ISamplePool } from '../../../../Model/index.js'
import type { PoolColumnDetection, TypeColumnDetection } from '../../../../Util/sheetItemRoles/index.ts'
import { detectPoolColumn, detectTypeColumn, judgePoolColumn, judgeTypeColumn } from '../../../../Util/sheetItemRoles/index.ts'
import { getSampleMetaDatas } from '../../../../Flux/Selector/Order/GetSampleMetaDatas/GetSampleMetaDataSelector.js'
import { TooltipTrigger } from '../../../../Provider/TooltipManager.js'
import { poolDisplayName } from '../../OrderManagementPage/PoolName.tsx'
import { CSVColSelector } from './CSVColSelector.js'
import type { CSVMappingResult } from './CSVModalTypes.js'
import { createRowMatcher } from './DetermineSampleOperations.js'
import type { SheetColumnsUsed } from './PlanCsvImport.ts'
import { planCsvImport } from './PlanCsvImport.ts'
import { fileKeyOf } from './SheetStructurePlan.ts'

type Identity = { sampleIdCol: string; sequencingCol: string; customerSampleIdCol: string }

type Options = {
  enabled: boolean
  rows: Record<string, string>[]
  headers: string[]
  pools: ISamplePool[]
  identity: Identity
  order?: IOrder
}

export type SheetStructureState = ReturnType<typeof useSheetStructure>

const rowKeys = (row: Record<string, string>, identity: Identity) => ({
  sampleId: identity.sampleIdCol ? row[identity.sampleIdCol] : undefined,
  sequenceId: identity.sequencingCol ? row[identity.sequencingCol] : undefined,
  customerSampleName: identity.customerSampleIdCol ? row[identity.customerSampleIdCol] : undefined
})

/**
 * Which columns state sample types and pools, detected until the user picks one: a choice made here beats
 * the detector. Rows the type column says nothing about are judged by their existing item's type.
 */
export const useSheetStructure = ({ enabled, rows, headers, pools, identity, order }: Options) => {
  const metaDatas = useSelector(getSampleMetaDatas)
  const [typeCol, setTypeCol] = useState('')
  const [typeTouched, setTypeTouched] = useState(false)
  const [poolCol, setPoolCol] = useState('')
  const [poolTouched, setPoolTouched] = useState(false)
  const { sampleIdCol, sequencingCol, customerSampleIdCol } = identity
  const identityColumns = useMemo(
    () => [sampleIdCol, sequencingCol, customerSampleIdCol].filter(Boolean),
    [sampleIdCol, sequencingCol, customerSampleIdCol]
  )

  const items = useMemo<(FileForProcessing | undefined)[]>(() => {
    if (!enabled) {
      return []
    }
    const matcher = createRowMatcher(
      pools.flatMap((pool) => [...pool.samples, ...pool.negativeControls]),
      false
    )
    return rows.map((row) => {
      const item = matcher.match(rowKeys(row, { sampleIdCol, sequencingCol, customerSampleIdCol }))
      return item && item !== 'no-key' ? item : undefined
    })
  }, [enabled, rows, pools, sampleIdCol, sequencingCol, customerSampleIdCol])

  const typeDetection = useMemo<TypeColumnDetection | undefined>(
    () => (enabled && typeCol ? judgeTypeColumn(typeCol, rows) : undefined),
    [enabled, typeCol, rows]
  )

  const poolContext = useMemo(
    () => ({
      isControl: (index: number) => (typeDetection?.roles[index] ?? items[index]?.itemType) === 'NEGATIVE_CONTROL',
      itemKey: (index: number) => {
        const item = items[index]
        return item ? fileKeyOf(item) : `row-${index}`
      },
      knownPoolNames: pools.map((pool, index) => poolDisplayName(pool, index))
    }),
    [typeDetection, items, pools]
  )

  const poolDetection = useMemo<PoolColumnDetection | undefined>(
    () => (enabled && poolCol ? judgePoolColumn(poolCol, rows, poolContext) : undefined),
    [enabled, poolCol, rows, poolContext]
  )

  useEffect(() => {
    if (enabled && !typeTouched) {
      const candidates = headers.filter((header) => !identityColumns.includes(header))
      setTypeCol(detectTypeColumn(candidates, rows)?.column ?? '')
    }
  }, [enabled, typeTouched, headers, rows, identityColumns])

  useEffect(() => {
    if (enabled && !poolTouched) {
      setPoolCol(detectPoolColumn(headers, rows, poolContext, [typeCol, ...identityColumns])?.column ?? '')
    }
  }, [enabled, poolTouched, headers, rows, poolContext, typeCol, identityColumns])

  const usedPool = poolDetection?.usable ? poolDetection : undefined
  const groupOfRow = useMemo(() => {
    const byRow = new Map<number, { key: string; name?: string }>()
    usedPool?.groups.forEach((group) =>
      group.rows.forEach((index) => byRow.set(index, { key: group.key, name: usedPool.namesUsable ? group.name : undefined }))
    )
    return byRow
  }, [usedPool])

  const sheetValues = useCallback(
    (index: number): { itemType?: EItemType; pool?: { key: string; name?: string } } =>
      enabled ? { itemType: typeDetection?.roles[index], pool: groupOfRow.get(index) } : {},
    [enabled, typeDetection, groupOfRow]
  )

  const columnsUsed = useCallback(
    (): SheetColumnsUsed => ({
      poolColumn: usedPool ? { auto: usedPool.auto, reason: usedPool.reason } : undefined,
      useNames: usedPool?.namesUsable ?? false
    }),
    [usedPool]
  )

  const preview = useMemo(() => {
    if (!enabled || (!typeDetection && !poolDetection)) {
      return undefined
    }
    const previewRows: CSVMappingResult[] = rows.map((row, index) => ({
      metadata: {},
      ...rowKeys(row, { sampleIdCol, sequencingCol, customerSampleIdCol }),
      ...sheetValues(index)
    }))
    try {
      return planCsvImport(
        pools,
        metaDatas,
        previewRows,
        { overrideCustomerData: false, overrideCustomerPrimer: false },
        'CREATE_SAMPLES',
        order,
        columnsUsed()
      )
    } catch (error) {
      console.warn('Could not preview the sheet import', error)
      return undefined
    }
  }, [
    enabled,
    typeDetection,
    poolDetection,
    rows,
    pools,
    metaDatas,
    order,
    sampleIdCol,
    sequencingCol,
    customerSampleIdCol,
    sheetValues,
    columnsUsed
  ])

  return {
    typeCol,
    poolCol,
    selectTypeCol: (column: string) => {
      setTypeTouched(true)
      setTypeCol(column)
    },
    selectPoolCol: (column: string) => {
      setPoolTouched(true)
      setPoolCol(column)
    },
    typeDetection,
    poolDetection,
    /** What the sheet says about one row's type and pool, for its {@link CSVMappingResult}. */
    sheetValues,
    columnsUsed,
    /** What the import would do to types and pools, and whether it will ask first. */
    assessment: preview?.assessment
  }
}

const ROLE_LABEL: Record<EItemType, string> = { SAMPLE: 'sample', NEGATIVE_CONTROL: 'negative control' }
const MAX_GROUPS_SHOWN = 8

const TypeEvidence = ({ detection }: { detection: TypeColumnDetection }) => (
  <div className="text-xs text-gray-500 col-span-2" data-testid="sheet-type-evidence">
    <p>{detection.reason}</p>
    <ul className="flex flex-wrap gap-x-3">
      {detection.values
        .filter((value) => value.role)
        .map((value) => (
          <li key={value.value}>
            <span className="font-mono">{value.value}</span> → {ROLE_LABEL[value.role as EItemType]} ({value.rows})
          </li>
        ))}
      {detection.emptyMeans && detection.emptyRows > 0 && (
        <li>
          empty → {ROLE_LABEL[detection.emptyMeans]} ({detection.emptyRows})
        </li>
      )}
    </ul>
    {detection.unmapped.length > 0 && (
      <p className="text-yellow-700 flex items-center gap-1">
        <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
        Not understood, these rows keep their type: {detection.unmapped.map((value) => `"${value}"`).join(', ')}
      </p>
    )}
  </div>
)

const PoolEvidence = ({ detection }: { detection: PoolColumnDetection }) => (
  <div className="text-xs text-gray-500 col-span-2" data-testid="sheet-pool-evidence">
    {detection.usable ? (
      <p className={detection.auto ? '' : 'text-yellow-700'}>{detection.reason}</p>
    ) : (
      <p className="text-yellow-700 flex items-center gap-1">
        <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
        This column cannot assign pools and is ignored: {detection.reason}
      </p>
    )}
    {detection.usable && (
      <ul>
        {detection.groups.slice(0, MAX_GROUPS_SHOWN).map((group) => (
          <li key={group.key}>
            <span className="font-semibold">{detection.namesUsable ? group.name : group.value}</span>: {group.rows.length} rows,{' '}
            {group.controls.length === 0
              ? 'no negative control'
              : `${group.controls.length} negative control${group.controls.length === 1 ? '' : 's'}`}
          </li>
        ))}
        {detection.groups.length > MAX_GROUPS_SHOWN && <li>… and {detection.groups.length - MAX_GROUPS_SHOWN} more</li>}
      </ul>
    )}
  </div>
)

type SectionProps = {
  state: SheetStructureState
  headers: string[]
}

/** Picks the type and pool columns and previews what they do to the order, before anything is written. */
export const SheetStructureSection = ({ state, headers }: SectionProps) => {
  const assessment = state.assessment

  return (
    <>
      <hr className="mt-2 mb-1" />
      <label className="mb-2 font-semibold">
        <TooltipTrigger
          tooltip="A column saying which rows are negative controls, and one grouping rows into pools (plate, run, batch...). Each pool's samples are checked against that pool's negative controls."
          withIcon
        >
          Sample Types and Pools
        </TooltipTrigger>
      </label>
      <div className="grid grid-cols-[auto_1fr] gap-2 items-center max-w-xl" data-testid="sheet-structure-section">
        <CSVColSelector
          label="Sample Type"
          testId="item-type"
          onChange={state.selectTypeCol}
          value={state.typeCol}
          options={headers}
          unwrapped
        />
        {state.typeDetection && <TypeEvidence detection={state.typeDetection} />}
        <CSVColSelector label="Pool" testId="pool" onChange={state.selectPoolCol} value={state.poolCol} options={headers} unwrapped />
        {state.poolDetection && <PoolEvidence detection={state.poolDetection} />}
      </div>
      {assessment && (
        <div className="text-sm mt-2 max-w-xl" data-testid="sheet-structure-preview">
          {assessment.hasChanges ? (
            <p>
              This import <span className="font-semibold">{assessment.summary}</span>.
            </p>
          ) : (
            <p className="text-gray-500">No change to sample types or pools.</p>
          )}
          {assessment.reasons.length > 0 && (
            <div className="text-yellow-700">
              <p className="flex items-center gap-1">
                <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
                You will be asked to confirm, because:
              </p>
              <ul className="list-disc ml-6">
                {assessment.reasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </>
  )
}
